'use client'

import 'react-google-reviews/dist/index.css'

import { useCallback, useEffect, useRef, useState } from 'react'
import { type ReactGoogleReview, ReactGoogleReviews } from 'react-google-reviews'
import { A11y } from 'swiper/modules'
import { Autoplay, Mousewheel } from 'swiper/modules'
import { Keyboard } from 'swiper/modules'
import { SwiperSlide } from 'swiper/react'
import { Swiper } from 'swiper/react'

export const GoogleReviews = ({ placeId }: { placeId: string }) => {
  const [reviews, setReviews] = useState<ReactGoogleReview[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [maxHeight, setMaxHeight] = useState<number>(0)
  const slideRefs = useRef<(HTMLDivElement | null)[]>([])
  const resizeObserver = useRef<ResizeObserver | null>(null)

  if (!placeId) {
    return null
  }

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await fetch(`/api/google-reviews?placeId=${encodeURIComponent(placeId)}`, {
          method: 'GET',
        })
        const data = await response.json()

        if (!response.ok) {
          throw new Error(data.error || 'Failed to fetch reviews')
        }

        if (!data.reviews || !Array.isArray(data.reviews)) {
          throw new Error('Invalid reviews data received')
        }

        const transformedReviews: ReactGoogleReview[] = (data.reviews ?? [])
          .filter((review: any) => review && typeof review === 'object') // keep only valid review objects
          .map((review: any) => ({
            reviewId: String(review.time ?? Date.now()),
            reviewer: {
              profilePhotoUrl: review.profile_photo_url ?? '',
              displayName: review.author_name ?? 'Anonymous',
              isAnonymous: !review.author_name,
            },
            starRating: Number(review.rating ?? 0),
            comment: String(review.text ?? ''),
            createTime: new Date(Number(review.time ?? Date.now()) * 1000).toISOString(),
            updateTime: new Date(Number(review.time ?? Date.now()) * 1000).toISOString(),
          }))

        setReviews(transformedReviews)
        setError(null)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load reviews'
        setError(errorMessage)
      } finally {
        setIsLoading(false)
      }
    }

    fetchReviews()
  }, [])

  // Memoized height calculation function
  const updateMaxHeight = useCallback(() => {
    if (!slideRefs.current.length) return

    // Use requestAnimationFrame to batch read operations
    requestAnimationFrame(() => {
      const heights = slideRefs.current.filter(Boolean).map((ref) => ref?.offsetHeight || 0)

      const newMaxHeight = Math.max(...heights)

      if (newMaxHeight > 0 && newMaxHeight !== maxHeight) {
        setMaxHeight(newMaxHeight)
      }
    })
  }, [maxHeight])

  // Setup resize observer
  useEffect(() => {
    if (!isLoading && reviews.length > 0) {
      // Cleanup previous observer
      if (resizeObserver.current) {
        resizeObserver.current.disconnect()
      }

      // Create new observer
      resizeObserver.current = new ResizeObserver(updateMaxHeight)

      // Observe all slide elements
      slideRefs.current
        .filter(Boolean)
        .forEach((ref) => ref && resizeObserver.current?.observe(ref))

      // Initial height calculation
      updateMaxHeight()
    }

    return () => {
      // Cleanup
      if (resizeObserver.current) {
        resizeObserver.current.disconnect()
      }
    }
  }, [reviews, isLoading, updateMaxHeight])

  if (isLoading) {
    return (
      <div className="w-full max-w-7xl mx-auto">
        <div className="space-y-8 mb-12">
          <div className="bg-white rounded-lg p-6 shadow-sm h-full">
            <div className="flex items-center gap-2 mb-4">
              {/* Google Logo Skeleton */}
              <div className="h-8 w-8 bg-gray-200 rounded animate-pulse" />
              {/* Star Rating Skeleton */}
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="w-6 h-6 bg-gray-200 rounded animate-pulse" />
                ))}
              </div>
            </div>
            {/* Review Text Skeleton */}
            <div className="space-y-2 flex-grow">
              <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse" />
              <div className="h-4 bg-gray-200 rounded w-full animate-pulse" />
              <div className="h-4 bg-gray-200 rounded w-2/3 animate-pulse" />
            </div>
            {/* Author Skeleton */}
            <div className="mt-4 h-4 bg-gray-200 rounded w-1/3 animate-pulse" />
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    //    return (
    // <div className="w-full max-w-7xl mx-auto px-4">
    //   <div className="text-red-500 bg-red-50 p-4 rounded-lg">Error loading reviews: {error}</div>
    // </div>
    //)
    console.warn(`Error loading reviews: ${error}`)
    return null
  }

  if (!reviews || reviews.length === 0) {
    return null
  }

  return (
    <div className="w-full max-w-7xl mx-auto">
      <ReactGoogleReviews
        reviews={reviews}
        layout="custom"
        renderer={(reviews) => (
          <div className="space-y-8 mb-10">
            <Swiper
              slidesPerView={1}
              spaceBetween={32}
              keyboard={{
                enabled: true,
              }}
              loop
              mousewheel={true}
              modules={[A11y, Keyboard, Mousewheel, Autoplay]}
              className="mt-6"
              autoplay={{
                delay: 5000,
                disableOnInteraction: false,
              }}
              onInit={() => {
                slideRefs.current = Array(reviews.length).fill(null)
              }}
            >
              {reviews.map(({ reviewer, comment, starRating }, index) => (
                <SwiperSlide key={index} className="h-full">
                  <div
                    ref={(el) => {
                      slideRefs.current[index] = el
                    }}
                    className="bg-white rounded-lg p-6 shadow-sm h-full flex flex-col"
                    style={{ minHeight: maxHeight > 0 ? `${maxHeight}px` : undefined }}
                  >
                    <div className="flex items-center gap-2 mb-4">
                      {/* Google Logo */}
                      <svg viewBox="0 0 24 24" className="h-8 w-8">
                        <path
                          fill="#4285F4"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="#34A853"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="#FBBC05"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="#EA4335"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                      {/* Star Rating */}
                      <div className="flex gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <svg
                            key={i}
                            className={`w-6 h-6 ${i < starRating ? 'text-yellow-400' : 'text-gray-300'}`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                    </div>
                    <div className="flex-grow flex items-center justify-center">
                      <p className="text-sm font-semibold italic text-start">
                        {comment ? <div>&quot;{comment}&quot;</div> : ''}
                      </p>
                    </div>
                    <footer className="text-sm text-neutral-900 text-start mt-4">
                      - {reviewer.displayName} (Google Review)
                    </footer>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}
      />
    </div>
  )
}
